import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { AddRounded } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import {
  FiqlLabelsInput,
  FiqlReference,
  FiqlSearch,
  OrdReferenceField,
  OrdDatagrid,
  OrdCreateDialog,
  OrdEditDialog,
  OrdShowDialog,
  OrdList,
  OrdIconStandard,
  useCanEdit,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { CalendarFields, PersonalCalendarReadFields, dataSchema } from '@ragu-components/ord-types';
import { ResourceContextProvider, TextField, useCanAccess, useListContext, useTranslate } from 'react-admin';

import { CalendarColor } from './CalendarColor';
import { CalendarsForm, PersonalCalendarsForm } from './CalendarsForm';

import type { ResourceType } from '../types';
import type { OrdListProps } from '@ragu-components/ord-components';
import type { Calendar } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

interface CalendarsGridProps {
  isPersonalResource: boolean;
  readFields: typeof PersonalCalendarReadFields | typeof CalendarFields;
  pendingSelectId: Identifier | undefined;
  onSelectedIds?: (selectedIds: Identifier[]) => void;
  onCalendars?: (calendars: Calendar[]) => void;
  onRowClick: (id: Identifier) => void;
  onShowClick?: (id: Identifier) => void;
  canEdit: boolean;
  canShow: boolean;
  clearPendingSelectId: () => void;
}

const CalendarsGrid = ({
  isPersonalResource,
  readFields,
  pendingSelectId,
  onSelectedIds,
  onCalendars,
  onRowClick,
  onShowClick,
  canEdit,
  canShow,
  clearPendingSelectId,
}: CalendarsGridProps): React.ReactElement => {
  const { data, selectedIds, onSelect } = useListContext<Calendar>();
  const canEditRecord = useCanEdit();
  const hasAutoSelectedRef = useRef(selectedIds.length !== 0);

  // Auto-select all calendars on first load
  useEffect(() => {
    if (selectedIds.length === 0 && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;
      onSelect((data ?? []).map((r) => r.id));
    }
  }, [data, onSelect, selectedIds.length]);

  // Auto-select newly created calendar
  useEffect(() => {
    if (pendingSelectId !== undefined && data !== undefined) {
      const id = pendingSelectId as string;
      if (!selectedIds.includes(id)) {
        onSelect([...selectedIds, id]);
      }
      clearPendingSelectId();
    }
  }, [data, onSelect, selectedIds, pendingSelectId, clearPendingSelectId]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectedIds?.(selectedIds);
  }, [selectedIds, onSelectedIds]);

  // Calendar records carry the labels that scope their events' permissions
  useEffect(() => {
    if (data !== undefined) onCalendars?.(data);
  }, [data, onCalendars]);

  return (
    <OrdDatagrid
      rowClick={(id, resource, record) => {
        // Label-scoped permissions are record-level: the user may edit only records
        // carrying one of their edit labels, and gets the readonly dialog otherwise
        if (canEdit && canEditRecord(resource, record)) onRowClick(id);
        else if (canShow) onShowClick?.(id);
        return false;
      }}
      // Selection here toggles which calendars' events are shown, not a bulk-edit target —
      // OrdDatagrid defaults selection to canEdit, which would hide it entirely for read-only users
      isRowSelectable={() => true}
      sx={{
        '& .RaBulkActionsToolbar-toolbar': { display: 'none' },
      }}
      showBulkCloneButton={false}
    >
      <TextField source={readFields.title} />
      {!isPersonalResource && <OrdReferenceField reference='users' source={CalendarFields.owner} />}
      <CalendarColor size={'18px'} />
    </OrdDatagrid>
  );
};

export interface CalendarsListProps extends Partial<OrdListProps>, ResourceType {
  onSelectedIds?: (selectedIds: Identifier[]) => void;
  onCalendars?: (calendars: Calendar[]) => void;
}

export const CalendarsList = ({
  isPersonalResource = true,
  onSelectedIds = undefined,
  onCalendars = undefined,
  ...props
}: CalendarsListProps): React.ReactElement => {
  const readFields = isPersonalResource ? PersonalCalendarReadFields : CalendarFields;
  const schema = isPersonalResource ? dataSchema.PersonalCalendarRead.title : dataSchema.Calendar.title;
  const calendarResource = isPersonalResource ? `me/${Resources.calendars}` : Resources.calendars;

  const translate = useTranslate();
  const { canAccess: canCreateAccess } = useCanAccess({ action: 'create', resource: calendarResource });
  const { canAccess: canEditAccess } = useCanAccess({ action: 'edit', resource: calendarResource });
  const { canAccess: canShowAccess } = useCanAccess({ action: 'show', resource: calendarResource });
  const canCreate = canCreateAccess === true;
  const canEdit = canEditAccess === true;
  const canShow = canShowAccess === true;
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<Identifier | undefined>(undefined);
  const [showingId, setShowingId] = useState<Identifier | undefined>(undefined);
  const [pendingSelectId, setPendingSelectId] = useState<Identifier | undefined>(undefined);

  const createButton = (
    <IconButton
      size='small'
      onClick={() => {
        setCreateOpen(true);
      }}
      sx={{ color: 'primary.dark' }}
      data-testid='create-calendar-button'
      aria-label={translate('resources.calendars.action.create', { _: 'Add calendar' })}
    >
      <AddRounded />
    </IconButton>
  );

  return (
    <>
      <OrdList
        icon={<OrdIconStandard name='calendars' />}
        hasCreate={false}
        hasExport={false}
        headerActions={canCreate ? createButton : undefined}
        headerChildren={<FiqlSearch schema={schema} />}
        quickFilters={
          isPersonalResource
            ? []
            : [
                <FiqlReference
                  key='owner'
                  label='resources.calendars.fields.owner'
                  resource={Resources.users}
                  source={CalendarFields['owner.id']}
                />,
                <FiqlLabelsInput key='labels' source={CalendarFields['labels.id']} />,
              ]
        }
        sy={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '& .RaList-main': {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          },
          '& .RaList-content': {
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
          },
        }}
        sort={{ field: CalendarFields.title, order: 'ASC' }}
        {...props}
      >
        <CalendarsGrid
          isPersonalResource={isPersonalResource}
          readFields={readFields}
          pendingSelectId={pendingSelectId}
          onSelectedIds={onSelectedIds}
          onCalendars={onCalendars}
          onRowClick={setEditingId}
          onShowClick={setShowingId}
          canEdit={canEdit}
          canShow={canShow}
          clearPendingSelectId={() => {
            setPendingSelectId(undefined);
          }}
        />
      </OrdList>
      <ResourceContextProvider value={calendarResource}>
        {/* Controlled dialogs mount their page controller even when closed, and react-admin
            access control redirects to /access-denied — render them only when allowed */}
        {canCreate && (
          <OrdCreateDialog
            resource={calendarResource}
            isOpen={createOpen}
            open={() => {
              setCreateOpen(true);
            }}
            close={() => {
              setCreateOpen(false);
            }}
            mutationOptions={{
              onSuccess: (data: { id: Identifier }) => {
                setPendingSelectId(data.id);
              },
            }}
          >
            {isPersonalResource ? <PersonalCalendarsForm /> : <CalendarsForm />}
          </OrdCreateDialog>
        )}
        {canEdit && (
          <OrdEditDialog
            resource={calendarResource}
            id={editingId ?? ''}
            isOpen={editingId !== undefined}
            open={() => {}} // controlled via setEditingId — no external open trigger needed
            close={() => {
              setEditingId(undefined);
            }}
          >
            {isPersonalResource ? <PersonalCalendarsForm /> : <CalendarsForm />}
          </OrdEditDialog>
        )}
        {canShow && (
          <OrdShowDialog
            resource={calendarResource}
            id={showingId ?? ''}
            isOpen={showingId !== undefined}
            open={() => {}}
            close={() => {
              setShowingId(undefined);
            }}
          >
            {isPersonalResource ? <PersonalCalendarsForm /> : <CalendarsForm />}
          </OrdShowDialog>
        )}
      </ResourceContextProvider>
    </>
  );
};

export default CalendarsList;
